/**
 * src/components/admin/adminPublish.js
 *
 * Publica los JSON de datos directamente en GitHub desde el panel admin.
 * Usa la API de contenidos de GitHub: cada publicación es un commit a main,
 * que dispara el deploy automático (GitHub Actions → Hostinger).
 *
 * ── Setup único (lo hace el programador) ─────────────────────────
 *  1. GitHub → Settings (perfil) → Developer settings
 *     → Fine-grained personal access tokens → Generate new token
 *  2. Repository access: "Only select repositories" → elegir este repo
 *  3. Permissions → Repository permissions → Contents: "Read and write"
 *  4. Generar y copiar el token (empieza con "github_pat_")
 *  5. Dárselo al administrador: el panel se lo pide la primera vez
 *     que use 🚀 Publicar y lo guarda en su navegador.
 */

import { showToast } from './adminHelpers.js'

const LS_KEY = 'adm-github'
const OWNER  = 'marioacalderon16-dev'
const REPO   = 'directorio'
const BRANCH = 'main'

function getToken() {
    return localStorage.getItem(LS_KEY)
}

function ensureToken() {
    let token = getToken()
    if (token) return token
    token = prompt(
        'Conexión con GitHub (solo la primera vez)\n\n' +
        'Pega el token de acceso que te dio el programador\n' +
        '(empieza con "github_pat_")'
    )?.trim()
    if (!token) return null
    localStorage.setItem(LS_KEY, token)
    return token
}

/** Permite cambiar el token guardado */
export function reconfigureGithub() {
    localStorage.removeItem(LS_KEY)
    const t = ensureToken()
    if (t) showToast('✅ Token de GitHub guardado')
}

/** Codifica un string UTF-8 a base64 */
function toBase64(str) {
    return btoa(unescape(encodeURIComponent(str)))
}

async function gh(path, options = {}) {
    const token = ensureToken()
    if (!token) throw new Error('Sin token de GitHub')
    const res = await fetch(`https://api.github.com/repos/${OWNER}/${REPO}/${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/vnd.github+json',
            ...(options.headers || {})
        }
    })
    if (res.status === 401 || res.status === 403) {
        localStorage.removeItem(LS_KEY)
        throw new Error('Token inválido o vencido. Vuelve a intentar y pega uno nuevo.')
    }
    return res
}

/**
 * Publica un archivo JSON en el repo (commit directo a main).
 * @param {string} repoPath - ruta en el repo, ej: 'public/data/businesses.json'
 * @param {Object} data - objeto a serializar como JSON
 * @param {string} message - mensaje del commit
 */
export async function publishFile(repoPath, data, message) {
    // 1. Obtener el SHA actual del archivo (necesario para actualizarlo)
    const getRes = await gh(`contents/${repoPath}?ref=${BRANCH}`)
    let sha
    if (getRes.ok) {
        sha = (await getRes.json()).sha
    } else if (getRes.status !== 404) {
        throw new Error(`Error leyendo ${repoPath}: ${getRes.status}`)
    }

    // 2. Subir el contenido nuevo
    const body = {
        message,
        content: toBase64(JSON.stringify(data, null, 2) + '\n'),
        branch: BRANCH,
        ...(sha ? { sha } : {})
    }
    const putRes = await gh(`contents/${repoPath}`, {
        method: 'PUT',
        body: JSON.stringify(body)
    })
    if (!putRes.ok) {
        const err = await putRes.json().catch(() => ({}))
        throw new Error(err.message || `Error ${putRes.status}`)
    }
    return putRes.json()
}

/**
 * Publica VARIOS archivos en UN SOLO commit atómico (Git Data API).
 *
 * ¿Por qué? Si se hacen dos commits separados (businesses + promos), cada uno
 * dispara su propio workflow de deploy. Ambos corren en paralelo y compiten por
 * escribir en la rama "dist": el que compiló con datos viejos puede ganar la
 * carrera y pisar al nuevo → se pierden cambios. Con un único commit hay un
 * único deploy y el problema desaparece de raíz.
 *
 * @param {Array<{path: string, data: Object}>} files - archivos a publicar
 * @param {string} message - mensaje del commit
 */
export async function publishFiles(files, message) {
    // Los blobs no dependen del estado de la rama: se crean una sola vez.
    const treeItems = []
    for (const { path, data } of files) {
        const blobRes = await gh('git/blobs', {
            method: 'POST',
            body: JSON.stringify({
                content: JSON.stringify(data, null, 2) + '\n',
                encoding: 'utf-8'
            })
        })
        if (!blobRes.ok) throw new Error(`No se pudo crear el contenido de ${path}: ${blobRes.status}`)
        const blobSha = (await blobRes.json()).sha
        treeItems.push({ path, mode: '100644', type: 'blob', sha: blobSha })
    }

    // Tree → commit → mover la rama. Si la rama se movió entremedio (otra
    // publicación), reintentamos leyendo el estado más reciente. Así dos
    // publicaciones seguidas nunca chocan con el error "does not match".
    const MAX_RETRIES = 5
    for (let intento = 1; intento <= MAX_RETRIES; intento++) {
        // 1. SHA del último commit de la rama (siempre el más reciente)
        const refRes = await gh(`git/ref/heads/${BRANCH}`)
        if (!refRes.ok) throw new Error(`No se pudo leer la rama ${BRANCH}: ${refRes.status}`)
        const latestCommitSha = (await refRes.json()).object.sha

        // 2. Árbol base de ese commit
        const commitRes = await gh(`git/commits/${latestCommitSha}`)
        if (!commitRes.ok) throw new Error(`No se pudo leer el commit base: ${commitRes.status}`)
        const baseTreeSha = (await commitRes.json()).tree.sha

        // 3. Árbol nuevo encima del árbol base actual
        const treeRes = await gh('git/trees', {
            method: 'POST',
            body: JSON.stringify({ base_tree: baseTreeSha, tree: treeItems })
        })
        if (!treeRes.ok) throw new Error(`No se pudo crear el árbol: ${treeRes.status}`)
        const newTreeSha = (await treeRes.json()).sha

        // 4. Commit con ambos archivos
        const newCommitRes = await gh('git/commits', {
            method: 'POST',
            body: JSON.stringify({ message, tree: newTreeSha, parents: [latestCommitSha] })
        })
        if (!newCommitRes.ok) throw new Error(`No se pudo crear el commit: ${newCommitRes.status}`)
        const newCommitSha = (await newCommitRes.json()).sha

        // 5. Mover la rama (sin force) — falla si alguien la movió entremedio
        const patchRes = await gh(`git/refs/heads/${BRANCH}`, {
            method: 'PATCH',
            body: JSON.stringify({ sha: newCommitSha, force: false })
        })
        if (patchRes.ok) return patchRes.json()

        // 422 = non-fast-forward: la rama avanzó. Reintentar con el estado nuevo.
        if (patchRes.status === 422 && intento < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 400 * intento))
            continue
        }

        const err = await patchRes.json().catch(() => ({}))
        throw new Error(err.message || `No se pudo actualizar la rama: ${patchRes.status}`)
    }
}
